import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Sparkles, Search, Code, PenTool, Send, ArrowRight, ChevronDown } from 'lucide-react';
import { useI18n } from '../i18n/useI18n';
import { peecAPI } from '../api/peecAPI';
import { Badge, Delta, DomainIdentity, FilterBar, PageHeader, PageShell, SectionCard, TableFrame, Td, Th } from '../components/PeecUI';
import { LLMIcon } from '../components/LLMIcon';
import { VisibilityChart } from '../components/charts/VisibilityChart';
import { DomainTypesChart } from '../components/charts/DomainTypesChart';

interface OverviewPageProps {
  workspaceId: string;
}

const flagUrl = (code: string) => {
  const lower = code.toLowerCase();
  if (lower === 'global' || lower === 'world' || lower.length !== 2) return '';
  return `https://flagcdn.com/${lower}.svg`;
};

const OverviewPage: React.FC<OverviewPageProps> = ({ workspaceId }) => {
  const { t } = useI18n();
  const [data, setData] = useState<any | null>(null);
  const [domesticLLMs, setDomesticLLMs] = useState<Array<{ id: string; name: string; icon: string }>>([]);
  const [internationalLLMs, setInternationalLLMs] = useState<Array<{ id: string; name: string; icon: string }>>([]);
  const [topicFilterOpen, setTopicFilterOpen] = useState(false);
  const [topicKeyword, setTopicKeyword] = useState('');
  const [allTopics, setAllTopics] = useState<string[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<string[]>(allTopics);
  const [timeRange, setTimeRange] = useState('全部时间');
  const [tagFilter, setTagFilter] = useState('全部标签');
  const [modelFilter, setModelFilter] = useState('全部模型');

  useEffect(() => {
    setSelectedTopics(allTopics);
  }, [allTopics]);

  const filteredTopics = useMemo(() => {
    const keyword = topicKeyword.trim().toLowerCase();
    if (!keyword) {
      return allTopics;
    }

    return allTopics.filter((topic) => topic.toLowerCase().includes(keyword));
  }, [allTopics, topicKeyword]);

  const visibleSelectedTopics = useMemo(
    () => filteredTopics.filter((topic) => selectedTopics.includes(topic)),
    [filteredTopics, selectedTopics],
  );

  const toggleTopic = (topic: string) => {
    setSelectedTopics((current) =>
      current.includes(topic) ? current.filter((item) => item !== topic) : [...current, topic],
    );
  };

  useEffect(() => {
    peecAPI.getOverview(workspaceId).then(setData).catch((error) => console.error('Failed to fetch overview:', error));
    peecAPI.getLLMPlatforms(workspaceId).then((platforms: any) => {
      setDomesticLLMs(platforms?.domestic ?? []);
      setInternationalLLMs(platforms?.international ?? []);
    }).catch((error) => console.error('Failed to fetch AI platforms:', error));
    peecAPI.getPrompts(workspaceId).then((prompts: any) => {
      setAllTopics((prompts?.topics ?? []).map((topic: { name: string }) => topic.name));
    }).catch((error) => console.error('Failed to fetch prompt topics:', error));
  }, [workspaceId]);

  const features = [
    {
      title: 'GEO Agent',
      desc: '自动调度全套工作流，一键串联从诊断、创作到分发闭环。',
      icon: Sparkles,
      bgGradient: 'from-indigo-100/80',
      iconColor: 'text-indigo-600',
      linkColor: 'text-indigo-600 hover:text-indigo-700',
      link: '#',
    },
    {
      title: 'AI 可见度诊断',
      desc: '多维度评估全网各大 AI 引擎中的品牌曝光率，精准挖掘流量洼地。',
      icon: Search,
      bgGradient: 'from-sky-100/60',
      iconColor: 'text-sky-600',
      linkColor: 'text-sky-600 hover:text-sky-700',
      link: '#',
    },
    {
      title: 'AI 站点优化',
      desc: '输出代码级可执行建议，支持源码托管与全自动修复，效果实时预览。',
      icon: Code,
      bgGradient: 'from-sky-100/60',
      iconColor: 'text-sky-600',
      linkColor: 'text-sky-600 hover:text-sky-700',
      link: '#',
    },
    {
      title: 'AI 图文工厂',
      desc: '构建专属企业内容库，完美兼顾海量矩阵外链铺发与深度精品长文创作。',
      icon: PenTool,
      bgGradient: 'from-sky-100/60',
      iconColor: 'text-sky-600',
      linkColor: 'text-sky-600 hover:text-sky-700',
      link: '#',
    },
    {
      title: '快捷投放',
      desc: '无缝打通核心渠道矩阵，一键向各大平台自动化极速发布，释放运营人力。',
      icon: Send,
      bgGradient: 'from-sky-100/60',
      iconColor: 'text-sky-600',
      linkColor: 'text-sky-600 hover:text-sky-700',
      link: '#',
    }
  ];

  if (!data) {
    return (
      <PageShell>
        <PageHeader title={t('pages.overview')} />
      </PageShell>
    );
  }

  const accountOverview = data.account_quick_overview;
  const visibilityDirection = accountOverview.visibility_delta.direction as 'up' | 'down' | 'flat';
  const visibilityTrendLabel = visibilityDirection === 'down' ? '下降' : visibilityDirection === 'flat' ? '持平' : '上涨';

  return (
    <PageShell>
      <PageHeader title={t('pages.overview')} />

      <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {features.map((feature, idx) => (
          <div 
            key={idx} 
            className="relative flex flex-col overflow-hidden rounded-xl bg-white shadow-sm transition-shadow hover:shadow-md p-5"
          >
            {/* Sweeping light effect for GEO Agent */}
            {idx === 0 && (
              <div className="pointer-events-none absolute inset-0 z-20 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/50 to-transparent skew-x-[-20deg]" />
            )}
            <div className={`absolute inset-0 bg-gradient-to-br ${feature.bgGradient} to-transparent pointer-events-none opacity-50`} />
            <div className="relative z-10 flex flex-1 flex-col">
              <div className="mb-2">
                <feature.icon className={`mb-3 h-8 w-8 ${feature.iconColor}`} strokeWidth={1.5} />
                <h3 className="text-[15px] font-semibold text-slate-900">{feature.title}</h3>
              </div>
              <p className="mb-5 flex-1 text-[13px] leading-relaxed text-slate-600">
                {feature.desc}
              </p>
              <div className="mt-auto">
                <a href={feature.link} className={`inline-flex items-center text-[13px] font-medium ${feature.linkColor}`}>
                  立即体验 <ArrowRight size={14} className="ml-1" />
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {accountOverview && (
        <SectionCard className="mb-8 overflow-hidden" title="账户快捷总览">
          <div className="grid gap-6 px-5 py-5 xl:grid-cols-[minmax(0,1fr)_360px] xl:items-center">
            <div>
              <p className="max-w-4xl text-[17px] font-medium leading-8 tracking-normal text-zinc-800 sm:text-lg sm:leading-9">
                你的品牌「
                <span className="mx-1.5 inline-flex items-center gap-1.5 align-[-0.2em] font-semibold text-zinc-950">
                  <img
                    className="h-5 w-5 shrink-0 rounded-md bg-white shadow-sm"
                    src={accountOverview.brand_icon_url}
                    alt=""
                    width={20}
                    height={20}
                    loading="lazy"
                  />
                  {accountOverview.brand_name}
                </span>
                」在{accountOverview.period_label}的综合 AI 可见性{visibilityTrendLabel}
                <span className="mx-2 align-baseline text-3xl font-semibold leading-none tabular-nums text-zinc-950 sm:text-4xl">
                  {accountOverview.visibility_delta.value}
                </span>
                ，当前在 topic「
                <span className="mx-1.5 font-semibold text-zinc-950">{accountOverview.topic.name}</span>
                」中平均排名
                <span className="mx-1.5 font-semibold tabular-nums text-zinc-950">
                  #{accountOverview.topic.average_rank.toFixed(1)}
                </span>
                ，在
                <span className="mx-1.5 font-semibold text-zinc-950">{accountOverview.platform.name}</span>
                平台{accountOverview.platform.highlight}。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4 border-t border-zinc-200 pt-4 xl:border-l xl:border-t-0 xl:pl-5 xl:pt-0">
              <div className="min-w-0">
                <div className="text-[11px] font-medium tracking-widest text-zinc-400">可见性</div>
                <div className="mt-1 text-2xl font-semibold leading-none tabular-nums text-zinc-950">
                  {accountOverview.visibility_delta.value}
                </div>
                <div className="mt-1 text-xs text-zinc-400">{accountOverview.period_label}</div>
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-medium tracking-widest text-zinc-400">平均排名</div>
                <div className="mt-1 text-2xl font-semibold leading-none tabular-nums text-zinc-950">
                  #{accountOverview.topic.average_rank.toFixed(1)}
                </div>
                <div className="mt-1 truncate text-xs text-zinc-400" title={accountOverview.topic.name}>topic</div>
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-medium tracking-widest text-zinc-400">平台</div>
                <div className="mt-1 truncate text-2xl font-semibold leading-none text-zinc-950" title={accountOverview.platform.name}>
                  {accountOverview.platform.name}
                </div>
                <div className="mt-1 truncate text-xs text-zinc-400" title={accountOverview.platform.highlight}>{accountOverview.platform.highlight}</div>
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      <div className="mb-8 overflow-hidden rounded-xl bg-white shadow-sm">
        <div className="px-5 py-4">
          <p className="text-[13px] text-zinc-500">{t('aiPlatforms.slogan')}</p>
        </div>
        <div className="grid grid-cols-2 divide-x divide-zinc-100">
          {/* 大陆 AI */}
          <div className="px-5 pb-5">
            <p className="mb-3 text-[11px] font-medium uppercase tracking-widest text-zinc-400">{t('aiPlatforms.domesticLabel')}</p>
            <div className="flex flex-wrap justify-evenly gap-y-3">
              {domesticLLMs.map((llm) => (
                <img
                  key={llm.id}
                  src={llm.icon}
                  alt={llm.name}
                  title={llm.name}
                  className="h-8 w-8 rounded-md object-contain transition-opacity hover:opacity-70"
                />
              ))}
            </div>
          </div>

          {/* 全球 AI */}
          <div className="px-5 pb-5">
            <p className="mb-3 text-[11px] font-medium uppercase tracking-widest text-zinc-400">{t('aiPlatforms.internationalLabel')}</p>
            <div className="flex flex-wrap justify-evenly gap-y-3">
              {internationalLLMs.map((llm) => (
                <img
                  key={llm.id}
                  src={llm.icon}
                  alt={llm.name}
                  title={llm.name}
                  className="h-8 w-8 rounded-md object-contain transition-opacity hover:opacity-70"
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-5">
        <h2 className="text-base font-semibold">{t('overview.title')}</h2>
        <p className="text-sm text-zinc-500">{t('overview.visibilityDesc')}</p>
      </div>

      <div className="mb-6">
        <FilterBar
          filters={[
            { key: 'time', label: t('filters.allTime'), options: ['全部时间', '过去7天', '过去30天', '过去90天'], value: timeRange, onChange: setTimeRange },
            { key: 'tag', label: t('filters.allTags'), options: ['全部标签', '加密货币', 'Web3', '量化交易', '区块链'], value: tagFilter, onChange: setTagFilter },
            { key: 'model', label: t('filters.allModels'), options: ['全部模型', 'ChatGPT', 'Perplexity', 'Claude', 'Gemini', 'Kimi', 'DeepSeek'], value: modelFilter, onChange: setModelFilter },
          ]}
          extra={
            <div className="relative">
              <button
                type="button"
                onClick={() => setTopicFilterOpen((open) => !open)}
                className="inline-flex h-8 items-center gap-1.5 rounded-full bg-white shadow-sm px-3.5 text-xs font-medium text-zinc-600 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1 hover:bg-zinc-50 transition-colors"
                aria-label={t('filters.filterTopics')}
                aria-expanded={topicFilterOpen}
              >
                <span>{selectedTopics.length === allTopics.length ? t('filters.allTopics') : `${selectedTopics.length} ${t('pages.topics')}`}</span>
                <ChevronDown size={12} className="text-zinc-400" aria-hidden="true" />
              </button>
              {topicFilterOpen && (
                <div className="absolute right-0 z-30 mt-2 w-72 rounded-xl border border-zinc-200 bg-white p-3 shadow-lg">
                  <input
                    value={topicKeyword}
                    onChange={(event) => setTopicKeyword(event.target.value)}
                    placeholder={t('filters.filterTopics')}
                    name="topicFilter"
                    autoComplete="off"
                    spellCheck={false}
                    className="mb-2 h-8 w-full rounded-lg border border-zinc-200 px-2.5 text-xs text-zinc-700 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                  />
                  <div className="max-h-56 space-y-1 overflow-auto">
                    {filteredTopics.map((topic) => {
                      const checked = selectedTopics.includes(topic);
                      return (
                        <label key={topic} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleTopic(topic)}
                            className="h-3.5 w-3.5 focus-visible:ring-zinc-400"
                          />
                          <span className="truncate">{topic}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          }
        />
        <div className="mt-3 flex flex-wrap gap-2">
          {visibleSelectedTopics.map((topic) => (
            <span
              key={topic}
              className="inline-flex h-8 items-center gap-1.5 rounded-full bg-white shadow-sm px-3.5 text-xs font-medium text-slate-600 outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-1 hover:bg-slate-50 transition-colors"
            >
              {topic}
            </span>
          ))}
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-2">
        <SectionCard className="flex h-[360px] min-h-0 min-w-0 flex-col overflow-hidden" title={t('pages.visibility')}>
          <div className="flex-1 min-h-0 min-w-0 px-5 pb-4 pt-2">
            <VisibilityChart className="h-full" />
          </div>
        </SectionCard>

        <SectionCard title={t('pages.topBrands')}>
          <TableFrame>
            <thead><tr><Th align="left">{t('pages.brand')}</Th><Th>{t('pages.visibility')}</Th><Th>{t('pages.sov')}</Th><Th>{t('pages.sentiment')}</Th><Th>{t('pages.position')}</Th></tr></thead>
            <tbody>
              {data.top_brands.map((brand: any) => (
                <tr key={brand.domain} className="hover:bg-zinc-50">
                  <Td align="left"><DomainIdentity rank={brand.rank} domain={brand.domain} name={brand.name} hideDomain /></Td>
                  <Td><span className="font-semibold">{brand.visibility}%</span><Delta /></Td>
                  <Td><span className="font-semibold">{brand.sov}%</span><Delta /></Td>
                  <Td>{brand.sentiment === null ? <span className="text-zinc-300">-</span> : <><span className="font-semibold">{brand.sentiment}</span><Delta /></>}</Td>
                  <Td>{brand.position === null ? <span className="text-zinc-300">-</span> : <><span className="font-semibold"># {brand.position.toFixed(1)}</span><Delta /></>}</Td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        </SectionCard>
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-2">
        <SectionCard className="flex h-[360px] flex-col overflow-hidden" title={t('pages.topDomains')} subtitle={t('pages.topDomainsSubtitle')} action={<a className="inline-flex items-center gap-1 text-sm font-medium text-zinc-700" href="#domains">{t('pages.allDomains')} <ExternalLink size={13} /></a>}>
          <TableFrame className="flex-1">
            <thead><tr><Th className="py-1.5" align="left">{t('pages.domain')}</Th><Th className="py-1.5">{t('pages.retrieved')}</Th><Th className="py-1.5">{t('pages.citationRate')}</Th><Th className="py-1.5">{t('pages.type')}</Th></tr></thead>
            <tbody>
              {data.top_domains.map((domain: any) => (
                <tr key={domain.id} className="hover:bg-zinc-50">
                  <Td className="py-1" align="left"><DomainIdentity rank={domain.rank} domain={domain.domain} /></Td>
                  <Td className="py-1 font-semibold">{domain.retrieved.toFixed(1)}%</Td>
                  <Td className="py-1">{domain.citation_rate.toFixed(1)}</Td>
                  <Td className="py-1"><Badge tone={domain.domain_type === 'Corporate' ? 'green' : domain.domain_type === 'UGC' ? 'purple' : 'slate'}>{t('pages.' + domain.domain_type.toLowerCase()) !== 'pages.' + domain.domain_type.toLowerCase() ? t('pages.' + domain.domain_type.toLowerCase()) : domain.domain_type}</Badge></Td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        </SectionCard>

        <SectionCard className="flex h-[360px] min-h-0 min-w-0 flex-col overflow-hidden" title={t('pages.domainTypes')} subtitle={`${t('pages.totalRetrievals')}: 3.0k`}>
          <div className="flex-1 min-h-0 min-w-0 px-5 pb-4 pt-2">
            <DomainTypesChart className="h-full" data={data.domain_types} />
          </div>
        </SectionCard>
      </div>

      <SectionCard className="mt-5" title={t('pages.recentChats')} subtitle={t('pages.whereAIGetsInfo')} action={<label className="flex items-center gap-2 text-sm text-zinc-600"><input type="checkbox" className="h-4 w-4" /> tradingbase {t('pages.mentionedCheckbox')}</label>}>
        <div className="divide-y divide-zinc-100">
          {data.recent_chats.map((chat: any) => (
            <article key={chat.id} className="grid gap-3 px-4 py-4 hover:bg-zinc-50 md:grid-cols-[170px_1fr_auto]">
              <div className="flex items-center gap-2.5 text-sm font-medium">
                <LLMIcon name={chat.model} size={24} showName={false} />
                {(() => {
                  const url = flagUrl(chat.location);
                  return url ? (
                    <span className="relative inline-flex shrink-0">
                      <img
                        src={url}
                        alt={chat.location}
                        className="h-4 w-[26px] rounded-[3px] object-cover ring-1 ring-black/5"
                        loading="lazy"
                      />
                      <span className="absolute -bottom-1 -right-1.5 rounded bg-white px-0.5 text-[8px] font-bold leading-[11px] text-zinc-600 shadow-sm ring-1 ring-zinc-200">
                        {chat.location}
                      </span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded bg-zinc-100 px-1 text-[9px] font-semibold text-zinc-500">
                      {chat.location}
                    </span>
                  );
                })()}
              </div>
              <div>
                <h3 className="font-semibold text-zinc-950">{chat.prompt}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-zinc-600">{chat.answer}</p>
                <div className="mt-3 flex flex-wrap gap-2">{chat.sources.map((source: string) => <Badge key={source}>{source}</Badge>)}</div>
              </div>
              <div className="text-sm text-zinc-500">{chat.time_ago}</div>
            </article>
          ))}
        </div>
      </SectionCard>
    </PageShell>
  );
};

export default OverviewPage;
